import { Button, Input, Result, Form } from 'antd'
import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { LoadingOutlined } from '@ant-design/icons'
import { contextCompanyIdSelector } from '../../../../../../redux/context/company/selectors'
import { useBackend } from '../../../../../../services/backend'
import { ApiSecret } from '../../types'
import { notificationAction } from '../../../../../../redux/middleware/actions'
import { useDocsURL } from '../../../../../../utils/docs'
import { FutursoftAuthentication } from './types'
import { AppDispatch } from '../../../../../../redux/store'
import DataUpdate from '../../dataUpdate'

interface FutursoftApiProps {
  allowAuthentication: boolean
  credentialsRequestUrl: string
  pageDocsUrl?: string
}

const FutursoftApi = ({ allowAuthentication, credentialsRequestUrl, pageDocsUrl }: FutursoftApiProps) => {
  const dispatch: AppDispatch = useDispatch()

  const docsURL = useDocsURL()
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const credentialsRequest = useBackend(credentialsRequestUrl, process.env.REACT_APP_INTEGRATION_URL)
  const [username, setUsername] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [authorized, setAuthorized] = useState(false)

  const getCredentialsHandler = () => {
    credentialsRequest
      .get({
        urlParams: { companyId }
      })
      .then((credentials: ApiSecret) => {
        credentials.companyId ? setAuthorized(true) : setAuthorized(false)
      })
      .catch(() => {
        setUsername('')
        setPassword('')
        setAuthorized(false)
      })
  }

  useEffect(getCredentialsHandler, [companyId])

  const submit = () => {
    credentialsRequest
      .put({
        urlParams: { companyId },
        body: {
          data: {
            username,
            password
          }
        }
      })
      .then((credentials: FutursoftAuthentication) => {
        setUsername(credentials.username)
        setPassword(credentials.password)
        setAuthorized(true)
        dispatch(
          notificationAction({
            type: 'success',
            message: 'AUTHENTICATION_SUCCESS'
          })
        )
      })
      .catch(() => {
        setAuthorized(false)
        dispatch(
          notificationAction({
            type: 'error',
            message: 'AUTHENTICATION_ERROR',
            description: 'USERNAME_PASSWORD_MISMATCH'
          })
        )
      })
  }

  const emptyCredentials = () => {
    setUsername('')
    setPassword('')
    setAuthorized(false)
  }

  if (authorized) {
    return (
      <>
        <Result
          status="success"
          title={t('integrationsPage:authenticated')}
          extra={
            allowAuthentication && (
              <>
                <Button onClick={emptyCredentials} type="primary">
                  {t('integrationsPage:authenticate')}
                </Button>
                <DataUpdate page="futursoft" />
              </>
            )
          }
        />
      </>
    )
  }
  if (allowAuthentication) {
    return (
      <Form layout="vertical">
        <Form.Item style={{ marginTop: 10 }} label={t('integrationsPage:username')}>
          <Input
            value={username}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUsername(event.target.value)}
            suffix={credentialsRequest.loading && <LoadingOutlined />}
          />
        </Form.Item>
        <Form.Item style={{ marginTop: 10 }} label={t('integrationsPage:password')}>
          <Input
            value={password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)}
            suffix={credentialsRequest.loading && <LoadingOutlined />}
          />
        </Form.Item>
        <Form.Item>
          <Button.Group>
            <Button type="primary" onClick={() => submit()} loading={credentialsRequest.loading}>
              {t('integrationsPage:authorize')}
            </Button>
          </Button.Group>
        </Form.Item>
      </Form>
    )
  }
  return (
    <Result
      status="info"
      title={t('integrationsPage:notAuthenticated')}
      extra={
        <a href={`${docsURL}${pageDocsUrl}`} target="_blank" rel="noopener noreferrer">
          {t('integrationsPage:fortnox-info')}
        </a>
      }
    />
  )
}

export default FutursoftApi
